/**
 * jxrl domain — catch-all /jxrl/{*operation} + typed wrappers for the ~23 ops
 * observed in production probes.
 *
 * Gotchas from api-wiring §2.2:
 *   - Use SLASHES not dashes in operation path: Deposit/AcctInq (not Deposit-AcctInq)
 *   - `Count` is case-sensitive (capital C) — callers must pass as-is in `queryParams`
 *   - `charset=utf-8` is stripped from outgoing Content-Type by the proxy
 *   - ParmValSrch canonical path is `Customer/ParmValSrch` (other category paths deprecated)
 *   - Proxy strips `code` from forwarded queries (function key stays in header)
 */

import type { RequestOptions, RawJxResponse } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface JxrlRequestOptions extends RequestOptions {
  /** Raw query string appended after `?` (e.g. `Count=50&StartIndex=0`). Preserves case. */
  queryParams?: string;
  /** Caller-supplied audit user id; appended as header if set. */
  auditUserId?: string;
}

/**
 * Pulls `Count` out of a body object and merges it into queryParams.
 * JHA's REST gateway maps `?Count=N` onto SrchMsgRqHdr.MaxRec — sending
 * Count in the body is silently dropped by JHA's parser and returns
 * "Must be greater than 0" because MaxRec is left null. Memory:
 * reference_jha_ofacsrch_wire_shape_20260509.md.
 *
 * Returns the body with Count stripped + the merged queryParams string.
 * If queryParams already contains a `Count=` key the caller wins;
 * we don't double-up.
 */
function extractCountFromBody<TReq>(
  body: TReq,
  existingQueryParams?: string,
): { count: number | undefined; queryParams: string | undefined; bodyOut: TReq } {
  const hasExistingCount = !!existingQueryParams && /(^|[?&])Count=/i.test(existingQueryParams);
  if (hasExistingCount || body == null || typeof body !== 'object' || Array.isArray(body)) {
    return { count: undefined, queryParams: existingQueryParams, bodyOut: body };
  }
  const rec = body as unknown as Record<string, unknown>;
  if (!('Count' in rec)) {
    return { count: undefined, queryParams: existingQueryParams, bodyOut: body };
  }
  const countVal = rec.Count;
  const count =
    typeof countVal === 'number' ? countVal :
    typeof countVal === 'string' && /^\d+$/.test(countVal) ? parseInt(countVal, 10) :
    undefined;
  if (count === undefined || count <= 0) {
    return { count: undefined, queryParams: existingQueryParams, bodyOut: body };
  }
  // Strip Count from a SHALLOW copy of the body so callers don't see mutation.
  const stripped: Record<string, unknown> = { ...rec };
  delete stripped.Count;
  const merged = existingQueryParams && existingQueryParams.length > 0
    ? `${existingQueryParams}&Count=${count}`
    : `Count=${count}`;
  return { count, queryParams: merged, bodyOut: stripped as unknown as TReq };
}

function buildJxrlPath(operation: string, queryParams?: string): string {
  // Never allow a leading slash that would break the `/jxrl/` prefix
  const clean = operation.replace(/^\/+/, '');
  const base = `/jxrl/${clean}`;
  if (!queryParams) return base;
  const trimmed = queryParams.replace(/^[?&]+/, '');
  return trimmed.length ? `${base}?${trimmed}` : base;
}

export class JxrlApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * Catch-all — forward `operation` to jXRL verbatim.
   * Use slashes in operation path: `Deposit/AcctInq`, `Customer/OFACSrch`, etc.
   */
  public post<TReq = Record<string, unknown>, TResp = RawJxResponse>(
    operation: string,
    body: TReq,
    opts?: JxrlRequestOptions
  ): Promise<TResp> {
    const path = buildJxrlPath(operation, opts?.queryParams);
    const headers: Record<string, string> = { ...(opts?.headers ?? {}) };
    if (opts?.auditUserId) headers['x-pcb-audit-user-id'] = opts.auditUserId;
    const signal = opts?.signal;
    // Fix A (Bill 2026-06-03): default `retry:false` for POST so non-idempotent
    // WRITES (AcctAdd/AcctMod/CustMsgAdd/…) never auto-retry on a 502 — a retried
    // write could double-submit to Core Director. But HONOR an explicit
    // opts.retry, so idempotent reads can opt in (see postRead). Previously this
    // hard-coded `retry:false` and silently DISABLED http.ts's built-in jXRL
    // 5xx cold-start retry (http.ts:208 `opts.retry !== false`) for EVERY call —
    // which is why the first cold OFACSrch 502 surfaced instead of self-healing.
    return this.http.post<TResp>(path, {
      body: body as unknown,
      headers,
      ...(signal ? { signal } : {}),
      retry: opts?.retry ?? false,
    });
  }

  public get<TResp = RawJxResponse>(
    operation: string,
    opts?: JxrlRequestOptions
  ): Promise<TResp> {
    const path = buildJxrlPath(operation, opts?.queryParams);
    const headers: Record<string, string> = { ...(opts?.headers ?? {}) };
    if (opts?.auditUserId) headers['x-pcb-audit-user-id'] = opts.auditUserId;
    const signal = opts?.signal;
    // Fix A: GET is idempotent — leave retry undefined so http.ts's jXRL
    // cold-start 5xx retry applies (opts.retry !== false), unless a caller
    // explicitly disables it.
    return this.http.get<TResp>(path, {
      headers,
      ...(signal ? { signal } : {}),
      ...(opts?.retry !== undefined ? { retry: opts.retry } : {}),
    });
  }

  /**
   * Idempotent-read POST — opts INTO the http.ts jXRL transient-5xx retry
   * (safe to repeat: OFACSrch / *Inq / *Srch return the same result). Writes
   * keep the safe `post()` default (no retry). A caller can still force
   * `retry:false` via opts. Fix A (Bill 2026-06-03).
   */
  private postRead<TReq, TResp = RawJxResponse>(
    operation: string,
    body: TReq,
    opts?: JxrlRequestOptions
  ): Promise<TResp> {
    return this.post<TReq, TResp>(operation, body, { ...opts, retry: opts?.retry ?? true });
  }

  // ---------------------------------------------------------------------------
  // Typed wrappers for frequently-used ops (api-wiring §2.2 inventory)
  // ---------------------------------------------------------------------------

  /**
   * Customer/OFACSrch — JHA's REST gateway maps `?Count=N` onto
   * `SrchMsgRqHdr.MaxRec`. Sending `Count` in the BODY returns
   * "Must be greater than 0" (Element=OFACSrch.SrchMsgRqHdr.MaxRec).
   * Memory: reference_jha_ofacsrch_wire_shape_20260509.md.
   *
   * 2026-05-21 (Bill RCA) — wrapper now AUTO-PROMOTES `Count` from
   * body → query string so callers can pass it in the body for
   * ergonomics. Callers that already supply Count via
   * opts.queryParams are honored unchanged.
   */
  public ofacSrch<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    const { queryParams, bodyOut } = extractCountFromBody(body, opts?.queryParams);
    const mergedOpts: JxrlRequestOptions = { ...opts, queryParams };
    return this.postRead('Customer/OFACSrch', bodyOut, mergedOpts);
  }

  public taxPlnInq<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.postRead('Customer/TaxPlnInq', body, opts);
  }

  /**
   * TaxPlnInfoInq — plan-information inquiry (aggregate tax plan metadata).
   * Confirmed proxied via generic `/api/jxrl/{*operation}` in
   * `PCBJxChangeApi/Functions/JxChangeProxyFunction.cs`. The RIM flows use
   * this in the dual-call pattern alongside `Deposit/AcctInq` to hydrate
   * the full IRA detail view before offering operational actions.
   */
  public taxPlnInfoInq<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.postRead('Customer/TaxPlnInfoInq', body, opts);
  }

  public taxPlnBenfAdd<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Customer/TaxPlnBenfAdd', body, opts);
  }

  public taxPlnBenfMod<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Customer/TaxPlnBenfMod', body, opts);
  }

  public taxPlnBenfSrch<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.postRead('Customer/TaxPlnBenfSrch', body, opts);
  }

  /** TaxPlnBenfInq — alias for TaxPlnBenfSrch, kept for RIM flow parity with Bible naming. */
  public taxPlnBenfInq<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.postRead('Customer/TaxPlnBenfSrch', body, opts);
  }

  public taxPlnMod<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Customer/TaxPlnMod', body, opts);
  }

  /**
   * TaxPlnTranAdd — post an IRA transaction (contribution, distribution,
   * rollover, RMD withdrawal). Wire verified against the generic proxy
   * route `/api/jxrl/{*operation}`. Caller supplies transaction codes per
   * `IRA_TXN_CODES` (612 Regular, 614 Direct Rollover, 619 Prior Year,
   * 631 Normal W/D, 641/651 Early W/D, 654 Death, 638 Fed W/H, 665 State W/H).
   */
  public taxPlnTranAdd<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Customer/TaxPlnTranAdd', body, opts);
  }

  /**
   * Canonicalized ParmValSrch — ALWAYS routes through Customer/ParmValSrch.
   * Other category paths (Parameter/, ParameterValue/, DocumentImage/) are
   * deprecated per WIRING-GUIDE §11 Q3 even though proxy forwards them.
   */
  public parmValSrch<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.postRead('Customer/ParmValSrch', body, opts);
  }

  public svcDictSrch<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    // Canonical — Teller/SvcDictSrch; alternates (TellerSystem/, Customer/) are
    // driver-specific and caller can invoke .post(...) directly if needed.
    return this.postRead('Teller/SvcDictSrch', body, opts);
  }

  public lnAcctAdd<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Loan/LnAcctAdd', body, opts);
  }

  public lnAcctAddValidate<TReq = Record<string, unknown>>(
    body: TReq,
    opts?: JxrlRequestOptions
  ): Promise<RawJxResponse> {
    return this.post('Loan/LnAcctAddValidate', body, opts);
  }

  public custMsgAdd<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Customer/CustMsgAdd', body, opts);
  }

  public custMsgSrch<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.postRead('Customer/CustMsgSrch', body, opts);
  }

  /**
   * Inquiry/FileMainSrch — Core Director File Maintenance Search (REQ-80).
   * Returns the field-level change audit (FieldDesc, FieldOrigVal → FieldNewVal,
   * FileMainDt/Time, FileMainUsrId) for an account.
   *
   * Confirmed by live PROD probe (2026-06-08, DDA accounts with multi-page history):
   *   - GroupName is **Inquiry/** (NOT Customer/). Bare `JXRL/FileMainSrch`
   *     404s "No listener"; the op lives under Inquiry/.
   *   - Selector MUST be `InAcctId: { AcctId, AcctType }` — file maintenance is
   *     account-keyed. A bare `AcctId`/`AcctType` (un-wrapped) OR a `CustId`
   *     with no linked accounts returns the generic core fault
   *     `Code 1 "System error encountered"`.
   *   - `?Count=N` → SrchMsgRqHdr.MaxRec (typical max 3999); `NextOffset` → Cursor.
   *   - Response header values (MoreRec/SentRec/Cursor/TotRec) come back as STRINGS.
   *
   * Idempotent read → postRead (opts into the jXRL cold-start 5xx retry). `Count`
   * auto-promotes from body → query string, same ergonomics as ofacSrch. For the
   * CIF surface the caller fans out one call per account (see RCM/useFileMaint).
   */
  public fileMainSrch<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    const { queryParams, bodyOut } = extractCountFromBody(body, opts?.queryParams);
    const mergedOpts: JxrlRequestOptions = { ...opts, queryParams };
    return this.postRead('Inquiry/FileMainSrch', bodyOut, mergedOpts);
  }

  public custRelInq<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.postRead('Customer/CustRelInq', body, opts);
  }

  public acctBenfAdd<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Customer/AcctBenfAdd', body, opts);
  }

  public depAcctMod<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Deposit/AcctMod', body, opts);
  }

  /**
   * Deposit/AcctClose — terminate an open Core Director deposit account.
   * Used by RSM close-out (SDB AcctType=50) and any deposit product that
   * supports graceful termination. For SDB drilled close-outs pair with
   * `accounts.safedepVacate` to flip field 38 VACANT and set field 801
   * (keys-missing) per modules digest §RSM.
   */
  public acctClose<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Deposit/AcctClose', body, opts);
  }

  public depAcctAdd<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Deposit/AcctAdd', body, opts);
  }

  public depAcctAddValidate<TReq = Record<string, unknown>>(
    body: TReq,
    opts?: JxrlRequestOptions
  ): Promise<RawJxResponse> {
    return this.post('Deposit/AcctAddValidate', body, opts);
  }

  public timeDepAcctAdd<TReq = Record<string, unknown>>(
    body: TReq,
    opts?: JxrlRequestOptions
  ): Promise<RawJxResponse> {
    return this.post('TimeDeposit/AcctAdd', body, opts);
  }

  public safeDepBoxSrch<TReq = Record<string, unknown>>(
    body: TReq,
    opts?: JxrlRequestOptions
  ): Promise<RawJxResponse> {
    return this.postRead('SafeDeposit/SafeDepBoxSrch', body, opts);
  }

  public eftCardSrch<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.postRead('EFT/EFTCardSrch', body, opts);
  }

  public intnetFinInstIdAdd<TReq = Record<string, unknown>>(
    body: TReq,
    opts?: JxrlRequestOptions
  ): Promise<RawJxResponse> {
    return this.post('OnlineBanking/IntnetFinInstIdAdd', body, opts);
  }

  public addrAdd<TReq = Record<string, unknown>>(body: TReq, opts?: JxrlRequestOptions): Promise<RawJxResponse> {
    return this.post('Address/AddrAdd', body, opts);
  }
}
